/**
 * lib/klickliste.js — 🆕 26.09.2026 · Auftrag 29, R1/R3.
 *
 * Reine Funktionen für den Sprung ins Bild und die Klickliste im
 * Anker-Sammelmodus — ohne React, damit `craco test` sie prüfen kann.
 *
 *   /admin/wizard/<id>?anker=1&t=<Sekunden>[&liste=<name>&nr=<n>]
 *
 * Ohne `anker=1` bleibt der Wizard, wie er ist.
 */

const LISTEN_NAME = /^[A-Za-z0-9_.-]+$/;

/** Liest die Sprung-Parameter; Ungültiges wird zu `null`, nie zu NaN. */
export function sprungParameter(search) {
  const q = typeof search === "string" || search == null
    ? new URLSearchParams(search || "")
    : search;
  const anker = q.get("anker") === "1";
  const tRoh = q.get("t");
  const t = tRoh != null && tRoh.trim() !== "" ? Number(tRoh) : NaN;
  const liste = q.get("liste");
  const nrRoh = q.get("nr");
  const nr = nrRoh != null && /^\d+$/.test(nrRoh.trim()) ? Number(nrRoh) : NaN;
  return {
    anker,
    t: Number.isFinite(t) && t >= 0 ? t : null,
    liste: liste && LISTEN_NAME.test(liste) ? liste : null,
    nr: Number.isInteger(nr) && nr >= 1 ? nr : null,
  };
}

/** Der Pfad zu einem Zeitpunkt; mit `liste` und `nr` auch zur Leiste. */
export function sprungPfad({ seq, t, liste, nr }) {
  const q = new URLSearchParams();
  q.set("anker", "1");
  q.set("t", String(Number(t)));
  if (liste && nr != null) {
    q.set("liste", liste);
    q.set("nr", String(nr));
  }
  return `/admin/wizard/${encodeURIComponent(seq)}?${q.toString()}`;
}

const nachNr = (punkte) => [...(punkte || [])].sort((a, b) => a.nr - b.nr);

export function punktZu(punkte, nr) {
  return (punkte || []).find((p) => p.nr === nr) || null;
}

/** Der Punkt vor `nr` in Listenreihenfolge, gleich welcher Stand. */
export function vorheriger(punkte, nr) {
  const vor = nachNr(punkte).filter((p) => p.nr < nr);
  return vor.length ? vor[vor.length - 1] : null;
}

/**
 * Der nächste **offene** Punkt nach `nr` — gesetzte und unklickbare
 * werden übersprungen, die Sequenz darf wechseln. Am Ende der Liste geht
 * es von vorn weiter (`vonVorn: true`), bis vor `nr`. Gibt es keinen
 * offenen Punkt außer `nr` selbst, ist das Ergebnis `null`.
 */
export function naechsterOffener(punkte, nr) {
  const alle = nachNr(punkte);
  const offen = (p) => p.stand === "offen" && p.nr !== nr;
  const danach = alle.find((p) => p.nr > nr && offen(p));
  if (danach) return { punkt: danach, vonVorn: false };
  const davor = alle.find((p) => p.nr < nr && offen(p));
  return davor ? { punkt: davor, vonVorn: true } : null;
}

/**
 * Nachtrag 2 (Befund F2): Wechselt die Klickliste auf einen anderen Punkt,
 * beginnt der Anker-Sammelmodus leer — sonst bleiben die Geister des
 * vorigen Punkts auf ihren alten Bildkoordinaten liegen und fangen den
 * ersten Klick der neuen Linie ab.
 *
 * `vorher` und `jetzt` sind `{nr, t}` oder `null` (keine Klickliste).
 * Leeren nur bei einem **anderen** Punkt (`nr` oder `t` anders); derselbe
 * Punkt, der erste Aufruf und alles außerhalb der Klickliste: nicht.
 */
export function leerenBeiPunktwechsel(vorher, jetzt) {
  if (!vorher || !jetzt) return false;
  return vorher.nr !== jetzt.nr || vorher.t !== jetzt.t;
}

/**
 * Nachtrag 4 (Befund F3), T2: In der Klickliste unterbleibt die
 * automatische Vorschau-Lösung (`dry_run`) des Freien Zeichnens — sie
 * rechnete eine volle Lösung, bevor das Speichern dieselbe noch einmal
 * rechnete. Der Restfehler kommt mit der Antwort des Speicherns.
 * Außerhalb der Klickliste bleibt die Vorschau.
 */
export function vorschauLoesen(klickliste) {
  return !klickliste;
}

/** T3: so lange nach dem letzten Anker wartet die Selbstkalibrierung. */
export const SELFCAL_WARTEN_KLICKLISTE_S = 120;

/** T3: der Wert fürs Speichern — nur in der Klickliste, sonst keiner. */
export function selfcalWartenS(klickliste) {
  return klickliste ? SELFCAL_WARTEN_KLICKLISTE_S : undefined;
}

/**
 * Nachtrag 6 (Befund F4): die Meldung nach dem Speichern in der
 * Klickliste — aus der Antwort des Speicherns, nichts wird nachgerechnet.
 * `stufe` ∈ gruen / gelb / rot / seg_only / ohne; `art` ist die Art der
 * Meldung (success / warning / error / info).
 */
export function ampelMeldung(res) {
  const a = (res && res.anchor) || {};
  const q = (res && res.quality) || a.quality || null;
  if (a.status === "seg_only") {
    return {
      stufe: "seg_only",
      art: "error",
      text: "Nur als Segment gespeichert — dieser Punkt trägt keine Pose und "
        + "ist keine Wahrheit. Neu zeichnen, wo das Bild es hergibt.",
    };
  }
  if (!q || !q.grade) {
    return { stufe: "ohne", art: "info", text: "Anker gespeichert — ohne 3D-Ampel." };
  }
  // Nachtrag 7 (F4.1): Rot heißt zweierlei. Ohne Roundtrip ist der Anker
  // allein nicht prüfbar (zu wenig Linien für eine Kamerapose,
  // `pose_quality.py:15`) — die Selbstkalibrierung des Shots trägt ihn oft
  // trotzdem. Mit Roundtrip passt er räumlich nicht.
  if (q.grade === "rot") {
    if (q.pose_consistency_px == null) {
      return {
        stufe: "unpruefbar",
        art: "warning",
        text: "Allein nicht prüfbar (zu wenig Linien für eine Kamerapose). "
          + "Stimmen die Linien, behalten — eine weitere Linie macht ihn prüfbar.",
      };
    }
    return {
      stufe: "rot",
      art: "error",
      text: `Passt räumlich nicht (Pose-Roundtrip ${q.pose_consistency_px} px) — `
        + "Benennung und Seite prüfen, neu zeichnen.",
    };
  }
  const art = { gruen: "success", gelb: "warning", rot: "error" }[q.grade] || "info";
  const roundtrip = q.pose_consistency_px != null
    ? ` · Pose-Roundtrip ${q.pose_consistency_px} px`
    : " · keine physikalische Pose ableitbar";
  return { stufe: q.grade, art, text: `3D-Ampel ${q.grade}: ${q.label_de}${roundtrip}` };
}

/** „gesetzt 3 · unklickbar 1 · offen 191" */
export function fortschrittText(f) {
  const x = f || {};
  return `gesetzt ${x.gesetzt ?? 0} · unklickbar ${x.unklickbar ?? 0} · offen ${x.offen ?? 0}`;
}
